import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton } from "@mui/material";
import { type GridRenderCellParams, useGridApiContext } from "@mui/x-data-grid";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

type Props = GridRenderCellParams & {
	display: ReactNode;
	/** Trailing adornment (e.g. the "Edited" badge) rendered before the pencil. */
	adornment?: ReactNode;
};

/**
 * DAT-209 pencil-on-hover cell. Renders the formatted value plus an EditIcon that only appears on
 * row hover (via the parent grid's hover class) and starts cell edit mode on click. Used only for
 * cells in inline-editable rows.
 */
export const EditableHoverCell: FC<Props> = ({ id, field, display, adornment }) => {
	const apiRef = useGridApiContext();
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", justifyContent: "space-between" }}>
			<Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
				{adornment}
				<IconButton
					size="small"
					aria-label={t("pbj_model.staffing.edit.edit_cell")}
					data-testid={`pbj-staffing-edit-${field}-${id}`}
					onClick={(e) => {
						e.stopPropagation();
						apiRef.current.startCellEditMode({ id, field });
					}}
					// Hidden at rest; revealed when the row is hovered (parent sets the hover class).
					sx={{ opacity: 0, transition: "opacity 0.1s", ".MuiDataGrid-row:hover &": { opacity: 1 }, p: 0.25 }}
				>
					<EditIcon sx={{ fontSize: 14 }} />
				</IconButton>
			</Box>
		</Box>
	);
};
